import { CalcMethod, LotType } from '$types/enums'
import { type Catalog, catalogSchema } from '$lib/map/types/catalog'
import { mixed, number, object, string } from 'yup'
import { type Material, materialSchema } from '$lib/map/types/material'
import { formatVolume } from '$utils/formatters'
import type { Lot, MaterialPropertyInfoDto } from '$mapapp/data/data.model'
import type { AddressData } from '$types/addressData'
import { compressString } from '$utils/compress'

export type MiniLot = {
	type: LotType
	volume: number
	price?: number
	zip: string
	catalog: Catalog | null
	material: Material | null
	materialData?: string
	materialProperties?: MaterialPropertyInfoDto[]
	recyclingClasses?: string[]
}

export const EmptyMiniLot = () => ({
	type: LotType.DISPOSAL,
	volume: NaN,
	zip: null as unknown as string,
	catalog: null,
	material: null,
	materialData: ''
})

export type MiniLotMatchRequest = Omit<MiniLot, 'catalog' | 'material'> & {
	materialNodeId?: string
}

export function miniLotToMatchRequest(miniLot: MiniLot): MiniLotMatchRequest {
	return {
		type: miniLot.type,
		volume: miniLot.volume,
		price: miniLot.price,
		zip: miniLot.zip,
		materialNodeId: miniLot.material?.id,
		materialData: miniLot.materialData && compressString(miniLot.materialData)
	}
}

export function miniLotToLot(miniLot: MiniLot) {
	// omit the keys below, since they are not given or in the wrong format when having a minilot
	// with the two step cast, we can omit some keys but still treat it as a Lot
	return {
		id: 'minilot',
		pricePerTon: miniLot.price,
		volume: miniLot.volume,
		label: generateMinilotLabel(miniLot),
		type: miniLot.type,
		calcMethod: CalcMethod.NORMAL,
		timeframe: '',
		requesterSiteLocation: { city: '', zip: miniLot.zip, street: '' } as AddressData,
		offerSiteLocation: null,
		materialName: miniLot.material?.text ?? '',
		materialProperties: miniLot.materialProperties ?? [],
		recyclingClasses: miniLot.recyclingClasses ?? [],
		code: '',
		correlationId: ''
	} as Omit<
		Lot,
		'contact' | 'attachments'  | 'comments' | 'code'  | 'address' | 'optimizerMailsStatus'
	> as Lot
}

function generateMinilotLabel(miniLot: MiniLot) {
	return `${formatVolume(String(miniLot.volume), false, 0)} Tonnen | ${miniLot.material?.text ? miniLot.material.text + ' | ' : ''}${miniLot.zip}`
}

export const miniLotSchema = object({
	type: mixed<LotType>()
		.oneOf(Object.values(LotType), 'UI.validation.LotType.required')
		.required('UI.validation.LotType.required'),
	volume: number()
		.typeError('UI.validation.volume.typeError')
		.required('UI.validation.volume.required')
		.positive('UI.validation.volume.positive'),
	zip: string()
		.trim()
		.required('UI.validation.address.zip.required')
		.min(5, 'UI.validation.address.zip.tooShort')
		.max(5, 'UI.validation.address.zip.tooLong')
		.matches(/^\d{5}$/, 'UI.validation.address.zip.onlyNumbers'),
	price: number()
		.transform((value, originalValue) => (originalValue === '' ? undefined : value))
		.nullable()
		.notRequired()
		.test('price-positive', 'UI.validation.grossPricePerTon.min', (value) => {
			return value == null || Number.isNaN(value) || value > 0
		}),
	catalog: catalogSchema.required('UI.validation.catalog.id.required'),
	material: materialSchema.required('UI.validation.material.id.required'),
	materialData: string().notRequired()
})
